import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface WorkCardStep {
  id: string;
  step_number: number;
  name: string;
  instruction: string;
  instructions?: string;
  quality_checks?: string[];
  qualityChecks?: string[];
}

interface WorkCard {
  id: string;
  order_id: string;
  order_number: string;
  sidemark: string;
  item_number: number;
  unit_count: number;
  width: number | string;
  height: number | string;
  frame_color: string;
  color: string;
  dealer_company: string;
  dealer_name: string;
  status: string;
  parts_bag_verified: boolean;
  bay_number: string | null;
  bay_id: string | null;
  steps: WorkCardStep[];
  workstation_steps?: WorkCardStep[];
}

export default function ShippingWorkCardPage() {
  const { workstationId, workOrderId } = useParams<{ workstationId: string; workOrderId: string }>();
  const navigate = useNavigate();
  const [card, setCard] = useState<WorkCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [completedChecks, setCompletedChecks] = useState<Record<string, boolean>>({});
  const [partsBagVerified, setPartsBagVerified] = useState(false);
  const [verifyingPartsBag, setVerifyingPartsBag] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [skipReason, setSkipReason] = useState('');

  // Bay assignment
  const [showBayModal, setShowBayModal] = useState(false);
  const [bays, setBays] = useState<any[]>([]);
  const [loadingBays, setLoadingBays] = useState(false);
  const [assigningBay, setAssigningBay] = useState(false);

  const loadCard = useCallback(async () => {
    if (!workOrderId) return;
    try {
      const data = await api.getShippingWorkCard(workOrderId);
      setCard(data);
      setPartsBagVerified(data.parts_bag_verified || false);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load work card');
    } finally {
      setLoading(false);
    }
  }, [workOrderId]);

  useEffect(() => {
    loadCard();
  }, [loadCard]);

  const handleVerifyPartsBag = async () => {
    if (!card) return;
    try {
      setVerifyingPartsBag(true);
      await api.verifyPartsBag(card.order_id, card.item_number);
      setPartsBagVerified(true);
    } catch (err: any) {
      alert(err.message || 'Failed to verify parts bag');
    } finally {
      setVerifyingPartsBag(false);
    }
  };

  const handleComplete = async () => {
    if (!workOrderId) return;
    try {
      setCompleting(true);
      await api.completeShippingWorkOrder(workOrderId);
      navigate(`/stations/${workstationId}`);
    } catch (err: any) {
      alert(err.message || 'Failed to complete work order');
      setCompleting(false);
    }
  };

  const handleSkip = async () => {
    if (!workOrderId) return;
    try {
      setSkipping(true);
      await api.skipShippingWorkOrder(workOrderId, skipReason || undefined);
      setShowSkipModal(false);
      navigate(`/stations/${workstationId}`);
    } catch (err: any) {
      alert(err.message || 'Failed to skip work order');
      setSkipping(false);
    }
  };

  const toggleCheck = (stepId: string, checkIndex: number) => {
    const key = `${stepId}-${checkIndex}`;
    setCompletedChecks(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAssignBay = async () => {
    setShowBayModal(true);
    setLoadingBays(true);
    try {
      const data = await api.getBays();
      setBays(data);
    } catch (err: any) {
      alert(err.message || 'Failed to load bays');
    } finally {
      setLoadingBays(false);
    }
  };

  const confirmAssignBay = async (bayNumber: string) => {
    if (!card) return;
    try {
      setAssigningBay(true);
      await api.assignBay(bayNumber, card.order_id);
      setShowBayModal(false);
      loadCard();
    } catch (err: any) {
      alert(err.message || 'Failed to assign bay');
    } finally {
      setAssigningBay(false);
    }
  };

  const steps = card?.steps || card?.workstation_steps || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error && !card) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => navigate(`/stations/${workstationId}`)}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Queue
        </button>
        <div className="p-4 bg-red-900/50 border border-red-700 rounded-lg text-red-300">
          {error}
        </div>
      </div>
    );
  }

  if (!card) return null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Back button */}
      <button
        onClick={() => navigate(`/stations/${workstationId}`)}
        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Queue
      </button>

      {/* Order Info Header */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">{card.order_number}</h1>
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-900/50 text-indigo-300 border border-indigo-700">
                Unit {card.item_number}{card.unit_count ? ` of ${card.unit_count}` : ''}
              </span>
            </div>
            {card.sidemark && (
              <p className="text-lg text-gray-300">{card.sidemark}</p>
            )}
            <div className="flex flex-wrap gap-4 text-sm text-gray-400">
              <span>
                <span className="text-gray-500">Dimensions:</span>{' '}
                <span className="text-white">{card.width}" x {card.height}"</span>
              </span>
              <span>
                <span className="text-gray-500">Color:</span>{' '}
                <span className="text-white">{card.frame_color || card.color || '-'}</span>
              </span>
              {(card.dealer_company || card.dealer_name) && (
                <span>
                  <span className="text-gray-500">Dealer:</span>{' '}
                  <span className="text-white">{card.dealer_company || card.dealer_name}</span>
                </span>
              )}
            </div>
          </div>

          {/* Bay info */}
          <div className="flex-shrink-0">
            {card.bay_number ? (
              <div className="bg-blue-900/30 border border-blue-700 rounded-lg px-4 py-3 text-center">
                <div className="text-blue-400 text-xs font-medium uppercase">Assigned Bay</div>
                <div className="text-2xl font-bold text-blue-300 mt-1">{card.bay_number}</div>
              </div>
            ) : (
              <button
                onClick={handleAssignBay}
                className="px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-gray-300 hover:text-white hover:border-indigo-500 transition-colors"
              >
                <div className="text-xs font-medium uppercase">No Bay</div>
                <div className="text-sm mt-1">Assign Bay</div>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Parts Bag Verification */}
      <div className={`rounded-xl border p-6 ${
        partsBagVerified
          ? 'bg-green-900/20 border-green-700'
          : 'bg-yellow-900/20 border-yellow-700'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {partsBagVerified ? (
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-900/50 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            ) : (
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-900/50 flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            )}
            <div>
              <h3 className={`font-semibold ${partsBagVerified ? 'text-green-300' : 'text-yellow-300'}`}>
                Parts Bag Verification
              </h3>
              <p className={`text-sm ${partsBagVerified ? 'text-green-400/70' : 'text-yellow-400/70'}`}>
                {partsBagVerified
                  ? 'Parts bag has been verified and included with this unit.'
                  : 'Verify that the parts bag is included with this unit before proceeding.'}
              </p>
            </div>
          </div>
          {!partsBagVerified && (
            <button
              onClick={handleVerifyPartsBag}
              disabled={verifyingPartsBag}
              className="flex-shrink-0 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-500 disabled:opacity-50 transition-colors font-medium"
            >
              {verifyingPartsBag ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Verifying...
                </span>
              ) : (
                'Verify Parts Bag'
              )}
            </button>
          )}
        </div>
      </div>

      {/* Steps */}
      {steps.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Work Steps</h2>
          {steps.map((step) => {
            const checks = step.quality_checks || step.qualityChecks || [];
            return (
              <div key={step.id || step.step_number} className="bg-gray-800 rounded-xl border border-gray-700 p-5">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-900/50 flex items-center justify-center text-indigo-300 font-bold text-lg">
                    {step.step_number}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold text-lg">{step.name}</h3>
                    <p className="text-gray-400 mt-1 whitespace-pre-wrap">
                      {step.instruction || step.instructions || ''}
                    </p>

                    {/* Quality Checks */}
                    {checks.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-sm font-medium text-gray-500 uppercase">Quality Checks</p>
                        {checks.map((check: string, idx: number) => {
                          const checkKey = `${step.id || step.step_number}-${idx}`;
                          const isChecked = completedChecks[checkKey] || false;
                          return (
                            <label
                              key={idx}
                              className="flex items-center gap-3 cursor-pointer group"
                            >
                              <div
                                className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                  isChecked
                                    ? 'bg-green-600 border-green-600'
                                    : 'border-gray-600 group-hover:border-gray-400'
                                }`}
                                onClick={() => toggleCheck(String(step.id || step.step_number), idx)}
                              >
                                {isChecked && (
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              <span className={`text-sm ${isChecked ? 'text-green-300 line-through' : 'text-gray-300'}`}>
                                {check}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pb-8">
        <button
          onClick={handleComplete}
          disabled={completing}
          className="flex-1 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-500 disabled:opacity-50 transition-colors font-semibold text-lg"
        >
          {completing ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Completing...
            </span>
          ) : (
            'Complete Work Order'
          )}
        </button>
        <button
          onClick={() => setShowSkipModal(true)}
          className="px-6 py-3 bg-gray-700 text-gray-300 rounded-xl hover:bg-gray-600 hover:text-white transition-colors font-medium"
        >
          Skip
        </button>
      </div>

      {/* Skip Modal */}
      {showSkipModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Skip Work Order</h3>
            <p className="text-gray-400 mb-4">
              Provide a reason for skipping this work order (optional).
            </p>
            <textarea
              value={skipReason}
              onChange={(e) => setSkipReason(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Reason for skipping..."
            />
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowSkipModal(false); setSkipReason(''); }}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSkip}
                disabled={skipping}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-500 disabled:opacity-50 transition-colors"
              >
                {skipping ? 'Skipping...' : 'Skip Work Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bay Assignment Modal */}
      {showBayModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Assign Bay</h3>
            {loadingBays ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
              </div>
            ) : bays.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No bays available.</p>
            ) : (
              <div className="grid grid-cols-3 gap-3 max-h-64 overflow-y-auto">
                {bays.map((bay: any) => {
                  const isOccupied = bay.order_id || bay.orderId;
                  const bayNum = bay.bay_number || bay.bayNumber || bay.number;
                  return (
                    <button
                      key={bay.id || bayNum}
                      onClick={() => !isOccupied && confirmAssignBay(bayNum)}
                      disabled={isOccupied || assigningBay}
                      className={`p-3 rounded-lg border text-center transition-colors ${
                        isOccupied
                          ? 'border-gray-600 bg-gray-700/50 text-gray-500 cursor-not-allowed'
                          : 'border-green-700 bg-green-900/20 text-green-300 hover:bg-green-900/40 hover:border-green-500'
                      }`}
                    >
                      <div className="font-bold text-lg">{bayNum}</div>
                      <div className="text-xs mt-1">
                        {isOccupied ? 'Occupied' : 'Available'}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowBayModal(false)}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
